import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import React, { useContext } from "react";
import { convertToFloat } from "utils/formatNumber";
import { PaymentRowObj } from "..";
import InfoModal from "./InfoModal";
import RefundModal from "./RefundModal";
import StatusRender from "./StatusRender";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import { getImage } from "utils/commonFunction";
import { ClientContext } from "ClientProvider";
import CustomToolTip from "components/tooltips";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { Link } from "react-router-dom";
import Card from "components/card";

const columnHelper = createColumnHelper<PaymentRowObj>();

function PaymentsListDashboard(props: {
  tableData: any;
  fetchPayments: () => void;
  isLoading: boolean;
  paymentName: boolean;
  allGateways: any[];
}) {
  const { tableData, fetchPayments, isLoading, paymentName, allGateways } = props;
  const { singleRoleData } = useContext(ClientContext);

  const columns = [
    columnHelper.accessor("client", {
      id: "client",
      header: () => <span className="pld-th">Client</span>,
      cell: (info: any) => (
        <div className="pld-cell">
          <div className="pld-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "?"}</div>
          <span className="pld-client-name">{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("amount", {
      id: "amount",
      header: () => <span className="pld-th">Amount</span>,
      cell: (info: any) => (
        <div className="pld-amount-cell">
          <span className="pld-amount">{convertToFloat(info.getValue())} {info?.row?.original?.currency}</span>
          {info?.row?.original?.converted_amount && info?.row?.original?.converted_currency && (
            <span className="pld-converted">
              {Number(info?.row?.original?.converted_amount).toFixed(2)} {info?.row?.original?.converted_currency}
            </span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor("et_id", {
      id: "et_id_",
      header: () => <span className="pld-th">TRANSACTION ID</span>,
      cell: (info: any) => (
        <div className="pld-ids-cell">
          <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
        </div>
      ),
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: () => <span className="pld-th">INTERNAL ID</span>,
      cell: (info: any) => (
        <div className="pld-ids-cell">
          <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
        </div>
      ),
    }),
    columnHelper.accessor("gateway_id", {
      id: "gateway_id",
      header: () => <span className="pld-th">Gateway {paymentName ? "Name" : "Hash ID"}</span>,
      cell: (info: any) => (
        paymentName ? (
          <div className="pld-gateway">
            <img style={{ height: "auto", width: "16px" }} src={getImage(info?.cell?.row?.original?.gateway_name)} alt="" />
            <span className="pld-gateway-name">{info?.cell?.row?.original?.gateway_name}</span>
          </div>
        ) : (
          <ShortTruncateCopy info={info?.cell?.row?.original?.gateway_id} showCopy={true} />
        )
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => <span className="pld-th">Status</span>,
      cell: (info) => (
        <div className="pld-status-cell">
          {true ? (
            <CustomToolTip
              content={
                info?.row?.original?.payment_history?.length > 0 ? (
                  info?.row?.original?.payment_history?.map((hisData: any, index: number) => {
                    let errorMessage = "";
                    if (["DECLINED", "ERRORED", "FAILED"].includes(info.getValue() as string)) {
                      const gatewayResponse = info?.row?.original?.gateway_response?.gateway_response;
                      const errorKeys = ["errorMessage", "message", "decline_reason", "error_message", "failure_message", "failure_code"];
                      const findLastError = (obj: any): string | null => {
                        let last: string | null = null;
                        const search = (data: any) => {
                          if (!data || typeof data !== "object") return;
                          for (const key of Object.keys(data)) {
                            if (errorKeys.includes(key) && key !== "_content") last = data[key];
                            if (typeof data[key] === "object") search(data[key]);
                            if (key === "_content" && typeof data[key] === "string") {
                              try {
                                const p = JSON.parse(data[key]);
                                if (p.decline_reason) last = p.decline_reason;
                                else for (const k of Object.keys(p)) if (errorKeys.includes(k)) last = p[k];
                              } catch {}
                            }
                          }
                        };
                        search(obj); return last;
                      };
                      const err = findLastError(gatewayResponse);
                      errorMessage = typeof err === "string" ? err : "";
                    }
                    return (
                      <Card className="m-2 shadow" key={index}>
                        <div className="border-1 max-w-[70vh] border border-white p-3">
                          {index === 0 && (
                            <p>Created at {hisData?.created_at} by API call from [{hisData?.client} - {hisData?.ip}] with status {hisData?.status}
                              {["DECLINED", "ERRORED", "FAILED"].includes(hisData?.status) && (<><br />Reason: {errorMessage || "Reason not mentioned by the gateway"}</>)}
                            </p>
                          )}
                          {index !== 0 && (
                            <p>Status change from {hisData?.previous_status} to {hisData?.status} at {hisData?.status_changed_date} by {hisData?.client ? `System Api Call ${singleRoleData?.[0]?.payment?.value?.payment_show_method_name ? hisData?.gateway_name : info?.cell?.row?.original?.gateway_id || ""}` : `${hisData?.first_name || ""} ${hisData?.last_name || ""}`}
                              {["DECLINED", "ERRORED", "FAILED"].includes(hisData?.status) && (<><br />Reason: {errorMessage || "Reason not mentioned by the gateway"}</>)}
                            </p>
                          )}
                        </div>
                      </Card>
                    );
                  })
                ) : <span>No history available.</span>
              }
              trigger={<button className="pld-status-btn"><StatusRender status={info?.row?.original?.status} value={info.getValue()} /></button>}
              header={"Status History"}
            />
          ) : (
            <StatusRender status={info?.row?.original?.status} value={info.getValue()} />
          )}
        </div>
      ),
    }),
    columnHelper.accessor("et_id", {
      id: "et_id",
      header: () => <span className="pld-th">Actions</span>,
      cell: (info: any) => {
        const enRefund = allGateways?.find((g) => g.id === info?.row?.original?.gateway_id);
        return (
          <div className="pld-actions">
            {singleRoleData?.[0]?.payment?.value?.refund_payment_list && (
              <RefundModal info={info?.row?.original} transactionID={info?.row?.original?.id} fetchPayments={fetchPayments} enRefund={enRefund?.refund} />
            )}
            {singleRoleData?.[0]?.payment?.value?.view_payment_response_information && (
              <InfoModal info={info?.row?.original} />
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
  ];

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const table = useReactTable({ data, columns, state: { sorting }, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel() });

  return (
    <div className="pld-card">
      {/* Header */}
      <div className="pld-header">
        <div className="pld-header-left">
          <div className="pld-header-icon">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div>
            <h3 className="pld-title">Recent Transactions</h3>
            <p className="pld-subtitle">Latest payment activity</p>
          </div>
        </div>
        <Link to="/admin/transactions" className="pld-view-more">
          View all
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </Link>
      </div>

      {/* Table */}
      <div className="pld-table-wrap">
        {isLoading ? (
          <div className="pld-loading">
            <div className="pld-spinner" />
            <p className="pld-loading-txt">Loading transactions…</p>
          </div>
        ) : (
          <table className="pld-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} colSpan={h.colSpan} onClick={h.column.getToggleSortingHandler()} className="pld-thead-th">
                      <div className="pld-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="pld-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={12} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={12} /> : <RiArrowUpDownFill size={12} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, ri) => (
                <tr key={row.id} className={`pld-tbody-tr${ri % 2 === 0 ? "" : " pld-tbody-tr--alt"}`}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="pld-tbody-td">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
              {table.getRowModel().rows.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="pld-empty">
                      <div className="pld-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      </div>
                      <p className="pld-empty-title">No transactions found</p>
                      <p className="pld-empty-sub">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .pld-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }

        /* Header */
        .pld-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .pld-header-left { display: flex; align-items: center; gap: 12px; }
        .pld-header-icon {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .pld-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px;line-height:22px }
        .pld-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .pld-view-more {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 13px; font-weight: 700; color: #2563eb;
          background: #eff6ff; border: 1.5px solid #bfdbfe;
          border-radius: 9px; padding: 8px 14px;
          text-decoration: none; transition: all 0.15s;
        }
        .pld-view-more:hover { background: #dbeafe; border-color: #93c5fd; }

        /* Table */
        .pld-table-wrap { overflow-x: auto; }
        .pld-table { width: 100%; border-collapse: collapse; }

        .pld-thead-th {
          padding: 12px 16px 10px;
          background: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          text-align: left; cursor: pointer; white-space: nowrap;
          user-select: none;
        }
        .pld-th-inner { display: flex; align-items: center; gap: 5px; }
        .pld-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
        .pld-sort-icon { color: #94a3b8; display: flex; align-items: center; }
        .pld-thead-th:hover .pld-sort-icon { color: #2563eb; }

        .pld-tbody-tr { transition: background 0.12s; }
        .pld-tbody-tr:hover { background: #f8fafc; }
        .pld-tbody-tr--alt { background: #fafbfc; }
        .pld-tbody-tr--alt:hover { background: #f1f5f9; }
        .pld-tbody-td { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Cells */
        .pld-cell { display: flex; align-items: center; gap: 9px; }
        .pld-avatar {
          width: 30px; height: 30px; border-radius: 8px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; color: #2563eb; flex-shrink: 0;
        }
        .pld-client-name { font-size: 13px; font-weight: 600; color: #0f172a; }
        .pld-amount-cell { display: flex; flex-direction: column; gap: 2px;text-transform:uppercase;color: #0f172a; }
        .pld-ids-cell { display: flex; flex-direction: column; gap: 2px;font-weight:bold;color: #0f172a; }
        .pld-amount { font-size: 13.5px; font-weight: 700; color: #0f172a; }
        .pld-converted { font-size: 11.5px; color: #94a3b8; font-weight: 500; }
        .pld-gateway { display: flex; align-items: center; gap: 7px; }
        .pld-gateway-name { font-size: 13px; font-weight: bold; color: #374151; }
        .pld-status-cell { }
        .pld-status-btn { background: none; border: none; cursor: pointer; padding: 0;display: inline-flex;align-items: center;gap: 6px;white-space: nowrap;font-weight:bold}
        .pld-actions { display: flex; align-items: center; gap: 6px; }

        /* Empty */
        .pld-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 24px; gap: 10px; }
        .pld-empty-icon { width: 52px; height: 52px; border-radius: 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .pld-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .pld-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; }

        /* Loading */
        .pld-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 12px; }
        .pld-spinner { width: 28px; height: 28px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: pldSpin 0.7s linear infinite; }
        @keyframes pldSpin { to { transform: rotate(360deg); } }
        .pld-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        @media (max-width: 600px) {
          .pld-header { padding: 16px; }
          .pld-thead-th, .pld-tbody-td { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
}

export default PaymentsListDashboard;